/**
 * hexSummary — "what state is this set of cameras in?", reduced to the one badge a
 * group header or a site header can afford to show.
 *
 * The board's headers are read at a glance, from across a room, before anything else on
 * the canvas: an operator scanning a wall of honeycombs wants to know WHICH panel to look
 * at, not the full breakdown of each. So a header reports the single most urgent fact —
 * alerting beats offline beats all-clear — and the hexes underneath carry the detail.
 */
import type { HexCamera, HexCounter } from '../../api/contracts';
import type { HexColorState } from './types';

export interface HexSummary {
  total: number;
  /** Active alerts across these cameras — the number the header reports. */
  alerts: number;
  /** Cameras with at least one active alert. */
  alerting: number;
  /** Cameras with neither live nor recording running. */
  offline: number;
}

export interface SummaryInputs {
  colorStateFor: (camera: HexCamera) => HexColorState;
  counterFor: (camera: HexCamera) => HexCounter | undefined;
}

export function summarize(cameras: HexCamera[], { colorStateFor, counterFor }: SummaryInputs): HexSummary {
  let alerts = 0;
  let alerting = 0;
  let offline = 0;
  cameras.forEach((cam) => {
    const n = counterFor(cam)?.total || 0;
    if (n > 0) {
      alerts += n;
      alerting += 1;
    } else if (colorStateFor(cam) === 'offline') offline += 1;
  });
  return { total: cameras.length, alerts, alerting, offline };
}

export interface SummaryBadge {
  label: string;
  /** Tailwind classes for the badge pill. */
  className: string;
}

/**
 * The one badge a header shows: alerts first, then offline, then "All clear".
 *
 * It counts ALERTS, not alerting cameras — that is the number an operator is deciding
 * against, and it is the same number the hexes underneath add up to, so the header can't
 * appear to contradict the board it sits on.
 */
export function summaryBadge(s: HexSummary): SummaryBadge {
  if (s.alerts > 0) {
    return {
      label: `${s.alerts} alert${s.alerts === 1 ? '' : 's'}`,
      className: 'bg-red-500/15 text-red-600 dark:text-red-400',
    };
  }
  if (s.offline > 0) {
    return {
      label: `${s.offline} offline`,
      className: 'bg-slate-500/15 text-muted-foreground',
    };
  }
  return { label: 'All clear', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' };
}
